import { load as loadRecaptcha } from 'recaptcha-v3';

export default class FormEvent extends HTMLElement {
    whenLoaded = Promise.all([
        customElements.whenDefined('c-calendar'),
        customElements.whenDefined('calendar-range'),
        customElements.whenDefined('calendar-date')
    ]);

    constructor(m) {
        super(m);

        // Binding event handlers to the class instance
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onMailOptInChange = this.onMailOptInChange.bind(this);
        this.onGdprOptInChange = this.onGdprOptInChange.bind(this);
        this.onCalendarChange = this.onCalendarChange.bind(this);

        // Select key UI elements from the DOM
        this.$form = this.querySelector('[data-form]');
        this.$formInner = this.querySelector('[data-form-inner]');
        this.$feedback = this.querySelector('[data-form-feedback]');
        this.$feedbackMessage = this.querySelector('[data-form-feedback-message]');

        // Mail opt-in checkbox and reference input
        this.$mailOptInRef = this.querySelector('[data-mail-opt-in-ref]');
        this.$mailOptInCheckbox = this.querySelector('[data-mail-opt-in-checkbox]');

        // GDPR opt-in checkbox and reference input
        this.$gdprOptInRef = this.querySelector('[data-gdpr-opt-in-ref]');
        this.$gdprOptInCheckbox = this.querySelector('[data-gdpr-opt-in-checkbox]');

        // Error elements for displaying validation errors
        this.$gdprError = this.querySelector('#lead_gdpr_consent_granted_error');
        this.$dateLabel = this.querySelector('[data-date-label]');
        this.$dateError = this.querySelector('#lead_event_date_error');

        // Container for smooth scrolling to error messages
        this.$scrollContainer = this.closest('.u-modal_container');

        // Component state data
        this.isDateInvalid = false; // Tracks if the calendar date input is invalid
        this.isLoading = false; // Prevents multiple form submissions
        this.formErrors = false; // Tracks if there are form validation errors
        this.successFeedback = this.$form.dataset['successFeedback'];
        this.errorFeedback = this.$form.dataset['errorFeedback'];
        this.recaptchaPubkey = this.$form.dataset['recaptchaPubkey'];
        this.dateLabelString = this.$dateLabel.getAttribute('data-label');
    }

    // =============================================================================
    // Lifecycle methods
    // =============================================================================
    connectedCallback() {
        this.whenLoaded.then(() => {
            requestAnimationFrame(() => {
                this.$calendarRoot = this.querySelector('[data-calendar]');
                this.$calendar = this.$calendarRoot.$calendar;

                // Bind event listeners and initialize any UI state updates
                this.bindEvents();

                // Update the date label to reflect the current calendar value
                this.updateDateLabel(this.$calendar.value);
            });
        });
    }

    disconnectedCallback() {
        // Unbind event listeners when the component is disconnected
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        // Bind form submission event
        this.$form.addEventListener('submit', this.onFormSubmit);

        // Calendar, opt-in checkboxes change events
        this.$calendar?.addEventListener('change', this.onCalendarChange);
        this.$mailOptInCheckbox.addEventListener('change', this.onMailOptInChange);
        this.$gdprOptInCheckbox.addEventListener('change', this.onGdprOptInChange);
    }

    unbindEvents() {
        // Remove previously bound event listeners
        this.$form.removeEventListener('submit', this.onFormSubmit);
        this.$calendar?.removeEventListener('change', this.onCalendarChange);
        this.$mailOptInCheckbox.removeEventListener('change', this.onMailOptInChange);
        this.$gdprOptInCheckbox.removeEventListener('change', this.onGdprOptInChange);
    }

    // Handles changes in the calendar input
    onCalendarChange(e) {
        this.updateDateLabel(e.target.value);

        // Reset the calendar error if it was previously invalid
        if (this.isDateInvalid) {
            this.resetCalendarError();
        }
    }

    // Handles changes in the mail opt-in checkbox
    onMailOptInChange(e) {
        const isChecked = e.target.checked;
        this.$mailOptInRef.value = isChecked ? '1' : '0';
    }

    // Handles changes in the GDPR opt-in checkbox
    onGdprOptInChange(e) {
        const isChecked = e.target.checked;
        this.$gdprOptInRef.value = isChecked ? '1' : '0';

        // Reset error state if the checkbox is checked
        if (isChecked) {
            this.resetGdprError();
        }
    }

    // =============================================================================
    // Form submission handling
    // =============================================================================
    async onFormSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        if (this.isLoading) return;

        // Reset any previous error states
        this.resetGdprError();

        // Validate GDPR opt-in checkbox
        if (!this.$gdprOptInCheckbox.checked || this.$gdprOptInRef.value !== '1') {
            this.showGdprError();
            return;
        }

        // Validate and format the date input
        const date = this.checkAndformatDates();
        if (!date) return;

        // Collect and remove duplicate form data fields
        let formData = new FormData(this.$form);
        formData = this.removeDuplicateFormData(formData);
        formData.append('event_date', date);

        // Be ready to fetch
        this.formErrors = false;
        this.feedback(false);
        this.setLoading();

        // Add recaptcha token if applicable
        if (this.recaptchaPubkey) {
            const recaptcha = await loadRecaptcha(this.recaptchaPubkey, { autoHideBadge: true });
            const token = await recaptcha.execute();
            formData.append('g-recaptcha-response', token);
        }

        // Submit the form data via fetch
        fetch(this.$form.getAttribute('action'), {
            method: this.$form.method,
            body: formData
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) {
                    throw (this.formErrors = response.errors);
                }
            })
            .catch((e) => {
                e && console.error(e);

                // Provide default error feedback if no errors were returned
                if (!this.formErrors) {
                    this.formErrors = [
                        ...(e && e.data && typeof e.data === 'string' ? [{ message: e.data }] : []),
                        { message: this.errorFeedback }
                    ];

                    this.feedback(
                        this.formErrors.map((error) => error.message).join('<br>'),
                        'error'
                    );
                }
            })
            .finally((response) => {
                this.setLoading(false);

                // Display success feedback if there were no errors
                if (!this.formErrors) {
                    this.feedback(response && response.data ? response.data : this.successFeedback);
                    this.reset();
                }
            });
    }

    // =============================================================================
    // Methods
    // =============================================================================

    // Updates the date label with the selected date
    updateDateLabel(value) {
        this.$dateLabel.innerHTML = this.dateLabelString.replace('{date}', value);
    }

    // Validates the date input and handles errors
    checkAndformatDates() {
        this.$dateError.style.display = 'none';

        if (!this.$calendar.value) {
            // Show date error and focus the calendar input
            this.$dateError.style.display = 'block';
            this.$calendarRoot.setAttribute('aria-labelledby', 'lead_event_date_error');
            this.$calendarRoot.focus();

            // Smoothly scroll to the calendar error message
            this.$scrollContainer.scrollTo({
                top: this.$calendarRoot.offsetTop,
                behavior: 'smooth'
            });

            this.isDateInvalid = true;
            return;
        }

        // Reset the calendar error state
        this.resetCalendarError();
        return this.$calendar.value;
    }

    // Clears the calendar error state
    resetCalendarError() {
        this.isDateInvalid = false;
        this.$calendarRoot.setAttribute('aria-labelledby', 'lead[event_date]');
        this.$dateError.style.display = 'none';
    }

    // Shows the GDPR error message
    showGdprError() {
        this.$gdprError.style.display = 'block';
        this.$gdprOptInCheckbox.setAttribute('aria-invalid', 'true');
        this.$gdprOptInCheckbox.setAttribute('aria-describedby', 'lead_gdpr_consent_granted_error');
        this.$gdprOptInCheckbox.focus();

        // Smoothly scroll to the GDPR error message
        this.$scrollContainer.scrollTo({
            top: this.$gdprOptInCheckbox.offsetTop,
            behavior: 'smooth'
        });
    }

    // Resets the GDPR error state
    resetGdprError() {
        this.$gdprError.style.display = 'none';
        this.$gdprOptInCheckbox.removeAttribute('aria-invalid');
        this.$gdprOptInCheckbox.removeAttribute('aria-describedby');
    }

    // Removes duplicate keys from the FormData object
    removeDuplicateFormData(formData) {
        const uniqueData = new Map();

        for (let [key, value] of formData.entries()) {
            uniqueData.set(key, value);
        }

        const deduplicatedFormData = new FormData();
        for (let [key, value] of uniqueData) {
            deduplicatedFormData.append(key, value);
        }

        return deduplicatedFormData;
    }

    // Toggles the loading state of the form
    setLoading(value = true) {
        this.isLoading = value;
        this.classList.toggle('is-loading', this.isLoading);
    }

    // Displays feedback messages (success or error)
    feedback(message, type = 'success') {
        if (!message) {
            this.$feedback.classList.add('u-hidden');
            this.$feedback.removeAttribute('aria-live');
            return;
        }

        // Update feedback message and make it visible
        this.$feedback.classList.remove('u-hidden');
        this.$feedback.setAttribute('aria-live', 'polite');
        this.$feedbackMessage.innerHTML = message;

        // Remove previous feedback type classes and apply the new one
        for (let modifier of ['error', 'success']) {
            this.$feedback.classList.remove(`-${modifier}`);
            this.$form.classList.remove(`-${modifier}`);
        }
        this.$feedback.classList.add(`-${type}`);
        this.$form.classList.add(`-${type}`);

        // Focus to the feedback message for accessibility
        this.$feedbackMessage.focus();
    }

    // Resets the form and all related UI states
    reset() {
        this.$form.reset();
        this.$calendarRoot.reset();
        this.updateDateLabel('');
        this.resetCalendarError();
        this.resetGdprError();
    }
}
